import { kleurIndex } from "./StaafGrafiek";

/**
 * Een ringdiagram met het totaal in het midden en een legenda met aantallen ernaast.
 *
 * <p>Een ring is alleen eerlijk als de delen samen het geheel zijn. Daarom staat het totaal in het
 * midden: dan is elk segment na te rekenen, en is meteen zichtbaar of de percentages ergens op
 * gebaseerd zijn. Zonder dat getal is een ring een plaatje waarvan je de noemer niet kent.
 *
 * <p><b>Kleine delen verdwijnen niet.</b> Een categorie van één procent is op deze maat nauwelijks
 * een streepje, maar hij staat wél in de legenda met zijn aantal. Weglaten of samenvoegen onder
 * "overig" zou het scherm laten kiezen wat de moeite waard is, en die keuze hoort niet hier.
 */

export interface DonutDeel {
  label: string;
  waarde: number;
}

/**
 * De delen als segmenten van de ring: elk met een begin- en eindhoek.
 *
 * <p>Losgetrokken van het tekenen zodat het na te rekenen is. Het gaat over hoeken van een cirkel;
 * dat is precies het soort rekenwerk dat er op het scherm goed uitziet en toch niet klopt.
 */
export function segmentenVan(delen: DonutDeel[]): {
  segmenten: { label: string; waarde: number; van: number; tot: number; deel: number }[];
  totaal: number;
} {
  const totaal = delen.reduce((som, d) => som + Math.max(d.waarde, 0), 0);
  if (totaal <= 0) {
    return { segmenten: [], totaal: 0 };
  }
  let hoek = 0;
  const segmenten = delen
    .filter((d) => d.waarde > 0)
    .map((d) => {
      const deel = d.waarde / totaal;
      const van = hoek;
      hoek += deel * 360;
      return { label: d.label, waarde: d.waarde, van, tot: hoek, deel };
    });
  return { segmenten, totaal };
}

/** Een punt op de cirkel. Hoek 0 is bovenaan, en met de klok mee. */
function punt(midden: number, straal: number, hoek: number): [number, number] {
  const rad = ((hoek - 90) * Math.PI) / 180;
  return [midden + straal * Math.cos(rad), midden + straal * Math.sin(rad)];
}

export function DonutGrafiek({ delen, eenheid, toon }: {
  delen: DonutDeel[];
  /** Wat er onder het totaal staat, bijvoorbeeld "dieren". */
  eenheid: string;
  toon: (w: number) => string;
}) {
  const { segmenten, totaal } = segmentenVan(delen);
  if (segmenten.length === 0) {
    return null;
  }

  const M = 110;
  const buiten = 100;
  const binnen = 66;

  return (
    <div className="fawkes-donut">
      <div className="fawkes-donut__ring">
        <svg viewBox={`0 0 ${M * 2} ${M * 2}`} role="img"
          aria-label={`${toon(totaal)} ${eenheid}`}>
          {segmenten.map((s, i) => {
            // Eén segment dat de hele ring vult kan niet als boog getekend worden: begin- en
            // eindpunt vallen samen en het pad wordt leeg. Dan twee halve ringen.
            const vol = s.deel >= 0.9999;
            const stukken = vol
              ? [{ van: 0, tot: 180 }, { van: 180, tot: 360 }]
              : [{ van: s.van, tot: s.tot }];
            return (
              <g key={s.label + i} className={`fawkes-donut__deel--${kleurIndex(s.label, i)}`}>
                {stukken.map((st, j) => {
                  const [x1, y1] = punt(M, buiten, st.van);
                  const [x2, y2] = punt(M, buiten, st.tot);
                  const [x3, y3] = punt(M, binnen, st.tot);
                  const [x4, y4] = punt(M, binnen, st.van);
                  const groot = st.tot - st.van > 180 ? 1 : 0;
                  return (
                    <path
                      key={j}
                      d={`M ${x1} ${y1} A ${buiten} ${buiten} 0 ${groot} 1 ${x2} ${y2}`
                        + ` L ${x3} ${y3} A ${binnen} ${binnen} 0 ${groot} 0 ${x4} ${y4} Z`}
                    >
                      <title>{`${s.label}: ${toon(s.waarde)}`}</title>
                    </path>
                  );
                })}
              </g>
            );
          })}
          {/* Het totaal in het midden: zonder de noemer is een percentage niet na te rekenen. */}
          <text className="fawkes-donut__totaal" x={M} y={M - 2} textAnchor="middle">
            {toon(totaal)}
          </text>
          <text className="fawkes-donut__eenheid" x={M} y={M + 18} textAnchor="middle">
            {eenheid}
          </text>
        </svg>
      </div>

      <ul className="fawkes-donut__legenda">
        {segmenten.map((s, i) => (
          <li className="fawkes-donut__regel" key={s.label + i}>
            <i className={`fawkes-donut__stip fawkes-donut__deel--${kleurIndex(s.label, i)}`} />
            <span className="fawkes-donut__naam">{s.label}</span>
            <span className="fawkes-donut__getal">{toon(s.waarde)}</span>
            {/* Afgerond op een heel procent; de aantallen ernaast zijn het echte antwoord. */}
            <span className="fawkes-donut__procent">{Math.round(s.deel * 100)}%</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
